import React, { useState, useRef, useCallback } from 'react';
import { ProductData, ProductVariant } from '../../../types';
import { Button } from './Button';
import { 
    Edit3, Plus, X, Check, RefreshCcw, 
    Trash2, Palette, Ruler, Box, Truck, ShieldCheck, Layers, 
    Scissors, Grid, Link as LinkIcon, FileText, Upload,
    Download, Camera, ToggleLeft, DollarSign
} from 'lucide-react';
import { showToast } from '../../../utils/helpers';
import api from '../../../utils/api';
import { API_CONFIG } from '../../../utils/apiConfig';

interface EditorViewProps {
  initialData: ProductData;
  onConfirm: (data: ProductData) => void;
}

type AttributeType = 'colors' | 'sizes' | 'materials' | 'styles' | 'patterns';

// Constants for digital product upload
const ALLOWED_FILE_TYPES = {
  'application/pdf': 'pdf',
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
  'application/zip': 'zip',
  'application/x-zip-compressed': 'zip'
};

const DELIVERY_TIMELINES = [
  { value: '1', label: '1 day' },
  { value: '2', label: '2 days' },
  { value: '3', label: '3 days' },
  { value: '7', label: '1 week' },
  { value: '14', label: '14 days' },
  { value: '21', label: '21 days' }
];
const RETURN_POLICIES = [
  { value: '0', label: 'No returns accepted' },
  { value: '7', label: '7 days return policy' },
  { value: '14', label: '14 days return policy' },
  { value: '30', label: '30 days return policy' }
];

const MAX_FILE_SIZES = {
  'pdf': 50 * 1024 * 1024, // 50MB
  'image': 30 * 1024 * 1024, // 30MB
  'zip': 100 * 1024 * 1024 // 100MB
};

export const EditorView: React.FC<EditorViewProps> = ({ initialData, onConfirm }) => {
  const [formData, setFormData] = useState<ProductData>(initialData);
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'specs' | 'logistics' | 'variants'>('details');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const digitalFileInputRef = useRef<HTMLInputElement>(null);

  // Digital file upload states
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [showTimelineDropdown, setShowTimelineDropdown] = useState(false);
  const [hasUploadedFile, setHasUploadedFile] = useState(false);
  const [digitalFilePreview, setDigitalFilePreview] = useState<string | null>(null);

  const handleChange = (field: keyof ProductData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  const handleTimelineSelect = useCallback((value) => {
    setFormData(prev => ({ ...prev, deliveryTimeline: value }));
    setShowTimelineDropdown(false);
  }, []);
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    handleChange('price', isNaN(val) ? 0 : val);
  };

  const handleArrayAdd = (field: AttributeType, value: string) => {
    if (value && !formData[field].includes(value)) {
        handleChange(field, [...formData[field], value]);
    }
  };

  const handleArrayRemove = (field: AttributeType, index: number) => {
    handleChange(field, formData[field].filter((_, i) => i !== index));
  };
  const handleReturnPolicyChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, returnPolicy: e.target.value }));
  }, []);

  const handleAddImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64 = reader.result as string;
            handleChange('images', [...formData.images, base64]);
        };
        reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleRemoveImage = (index: number) => {
    if (formData.images.length > 1) {
        handleChange('images', formData.images.filter((_, i) => i !== index));
    }
  };

  // Digital file validation
  const validateDigitalFile = (file: File) => {
    if (!file) {
      return { valid: false, error: 'No file provided' };
    }

    const fileType = ALLOWED_FILE_TYPES[file.type];
    if (!fileType) {
      return { 
        valid: false, 
        error: 'Invalid file type. Only PDF, images (JPEG, PNG, GIF, WebP), and ZIP files are allowed.' 
      };
    }

    const maxSize = MAX_FILE_SIZES[fileType];
    if (file.size > maxSize) {
      const maxSizeMB = maxSize / (1024 * 1024);
      return { 
        valid: false, 
        error: `File too large. Maximum size for ${fileType.toUpperCase()} files is ${maxSizeMB}MB.` 
      };
    }

    return { valid: true, fileType };
  };

  // Handle digital file upload to backend
  const handleDigitalFileUpload = async (file: File) => {
    if (!file) return null;

    // Reset previous state
    setUploadError('');
    setIsUploading(true);
    setUploadProgress(0);
    setDigitalFilePreview(null);

    const abortController = new AbortController();

    try {
      setUploadProgress(10);
      
      const fileValidation = validateDigitalFile(file);
      if (!fileValidation.valid) {
        throw new Error(fileValidation.error);
      }

      // Create preview for images
      if (fileValidation.fileType === 'image') {
        const previewUrl = URL.createObjectURL(file);
        setDigitalFilePreview(previewUrl);
      }

      setUploadProgress(20);

      // Prepare upload data
      const uploadData = new FormData();
      uploadData.append('file', file);

      setUploadProgress(30);

      // Upload to backend
      const uploadResponse = await api.post(API_CONFIG.PRODUCTS.UPLOAD, uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000,
        signal: abortController.signal,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const uploadPercentage = (progressEvent.loaded / progressEvent.total) * 60;
            setUploadProgress(30 + uploadPercentage);
          } else {
            setUploadProgress(prev => {
              const newProgress = prev + (100 - prev) * 0.1;
              return Math.min(newProgress, 90);
            });
          }
        },
      });

      setUploadProgress(95);

      const uploadResult = uploadResponse.data;
      
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || uploadResult.message || 'Upload failed');
      }

      const fileUrl = uploadResult.url || uploadResult.fileUrl || uploadResult.secure_url;
      const publicId = uploadResult.cloudinary_public_id;

      if (!fileUrl) {
        console.warn('Upload response missing URL:', uploadResult);
        throw new Error('Upload completed but no file URL returned');
      }

      setUploadProgress(100);
      setHasUploadedFile(true);
      
      // Update form data with uploaded file info
      handleChange('digitalProductLink', fileUrl);
      handleChange('digitalFileName', file.name);
      handleChange('digitalFileSize', file.size);
      handleChange('digitalFileType', file.type);
      handleChange('cloudinaryPublicId', publicId);

      showToast(`File uploaded successfully!`, 'success');
      
      setTimeout(() => {
        setUploadProgress(0);
        setIsUploading(false);
      }, 1500);

      return fileUrl;

    } catch (error) {
      if (digitalFilePreview) {
        URL.revokeObjectURL(digitalFilePreview);
      }
      
      console.error('Digital file upload error:', error);
      
      let errorMessage = 'Failed to upload file. Please try again.';
      
      if (error.name === 'AbortError') {
        errorMessage = 'Upload was cancelled.';
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Upload timed out. Please try a smaller file or check your connection.';
      } else if (error.response?.status === 413) {
        errorMessage = 'File too large. Please choose a smaller file.';
      } else if (error.response?.status === 415) {
        errorMessage = 'File type not supported. Please check the file format.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setUploadError(errorMessage);
      showToast(errorMessage, 'error');
      setUploadProgress(0);
      setIsUploading(false);
      setDigitalFilePreview(null);
      
      return null;
    }
  };

  const handleDigitalFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        await handleDigitalFileUpload(file);
    }
  };

  const removeDigitalFile = () => {
    handleChange('digitalProductLink', '');
    handleChange('digitalFileName', '');
    handleChange('digitalFileSize', 0);
    handleChange('digitalFileType', '');
    handleChange('cloudinaryPublicId', '');
    
    setDigitalFilePreview(null);
    setUploadProgress(0);
    setUploadError('');
    setHasUploadedFile(false);
    
    if (digitalFileInputRef.current) {
      digitalFileInputRef.current.value = '';
    }
  };

const generateVariants = () => {
  const attributesToCombine: {key: string, values: string[]}[] = [];
  
  if (formData.colors.length > 0) attributesToCombine.push({key: 'Color', values: formData.colors});
  if (formData.sizes.length > 0) attributesToCombine.push({key: 'Size', values: formData.sizes});
  if (formData.materials.length > 0) attributesToCombine.push({key: 'Material', values: formData.materials});
  // if (formData.styles.length > 0) attributesToCombine.push({key: 'Style', values: formData.styles});
  // if (formData.patterns.length > 0) attributesToCombine.push({key: 'Pattern', values: formData.patterns});

  if (attributesToCombine.length === 0) {
    alert("Add some attributes (Colors, Sizes, Materials, etc) first to generate variants.");
    return;
  }
  
  // Calculate total possible combinations
  const totalCombinations = attributesToCombine.reduce((acc, curr) => acc * curr.values.length, 1);
  
  // Limit to maximum 10 variants
  if (totalCombinations > 10) {
    alert(`Generating all ${totalCombinations} combinations would exceed the 10 variant limit.\n\nPlease reduce the number of attribute options to generate variants.`);
    return;
  }
  
  // Or if you want to generate only the first 10 combinations instead:
  // Uncomment the code below and remove the alert/return above
  
  /*
  if (totalCombinations > 10) {
    if (!window.confirm(`This would generate ${totalCombinations} variants, but will be limited to the first 10. Continue?`)) return;
  }
  */

  const cartesian = (a: any[], b: any[]) => [].concat(...a.map(d => b.map(e => [].concat(d, e))));
  
  let combinations: any[] = attributesToCombine[0].values.map(v => [{key: attributesToCombine[0].key, value: v}]);
  
  for (let i = 1; i < attributesToCombine.length; i++) {
    const next = attributesToCombine[i].values.map(v => ({key: attributesToCombine[i].key, value: v}));
    combinations = cartesian(combinations, next);
  }
  
  // Limit combinations to maximum 10
  combinations = combinations.slice(0, 10);

  const newVariants: ProductVariant[] = combinations.map((combo: any[], idx) => {
    const attributes: Record<string, string> = {};
    combo.forEach(c => attributes[c.key] = c.value);
    const name = Object.values(attributes).join(' / ');
    
    const existing = formData.variants.find(v => v.name === name);

    return {
      id: existing?.id || `var-${Date.now()}-${idx}`,
      name,
      price: existing?.price || formData.price,
      stock: existing?.stock || Math.max(1, Math.floor(formData.stock / (combinations.length || 1))),
      attributes
    };
  });

  handleChange('variants', newVariants);
};

  const updateVariant = (id: string, field: 'price' | 'stock', value: number) => {
      handleChange('variants', formData.variants.map(v => v.id === id ? { ...v, [field]: value } : v));
  };

  return (
<div className="flex flex-col lg:flex-row h-full w-full rounded-2xl animate-in slide-in-from-right-8 duration-500 bg-white  overflow-hidden transition-colors duration-300">
      {/* LEFT COLUMN: Images */}
      <div className="lg:w-1/2 h-auto lg:h-full overflow-y-auto p-4 lg:p-8 custom-scrollbar transition-colors duration-300">
        <div className="lg:max-w-xl mx-auto space-y-4">
            {/* Main Image Area */}
            <div className="relative w-full aspect-square rounded-3xl overflow-hidden shadow-lg border border-gray-200  group bg-white ">
                <img src={formData.images[0]} alt="Main" className="w-full h-full object-contain p-4" />
                
                {/* Price Overlay */}
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between z-10">
                     <span className="inline-block px-3 py-1 rounded-full bg-gray-900/80 backdrop-blur-md text-xs font-medium text-white border border-white/10 shadow-sm">
                        {formData.category}
                     </span>
                    <div className="relative">
  {isEditingPrice ? (
    <div className="flex items-center bg-white  text-gray-900  rounded-xl px-2 py-1 shadow-lg">
      <span className="font-bold text-lg">₦</span>
      <input 
        type="number" 
        value={formData.price} 
        onChange={handlePriceChange}
        onBlur={() => setIsEditingPrice(false)}
        autoFocus
        className="w-24 bg-transparent border-none outline-none font-bold text-xl p-0 ml-1 "
      />
    </div>
  ) : (
    <button onClick={() => setIsEditingPrice(true)} className="flex items-center bg-white  text-gray-900  px-4 py-2 rounded-xl font-bold text-xl shadow-lg active:scale-95 transition-transform hover:bg-white ">
      ₦{formData.price.toLocaleString()}
      <Edit3 className="w-3 h-3 ml-2 text-gray-400" />
    </button>
  )}
</div>
                </div>
            </div>

            {/* Multi-Image Strip */}
            <div className="flex gap-3 overflow-x-auto pb-2 lg:pb-0 px-0 scrollbar-hide">
                {formData.images.map((img, idx) => (
                    <div key={idx} className="relative flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-gray-200 group cursor-pointer bg-white ">
                        <img src={img} className="w-full h-full object-cover" />
                        <button 
                            onClick={() => handleRemoveImage(idx)}
                            className="absolute top-1 right-1 p-1 bg-red-500/90 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                        >
                            <X size={12} />
                        </button>
                    </div>
                ))}
                <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-shrink-0 w-20 h-20 rounded-xl border border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:text-brand-600  hover:border-brand-400  hover:bg-brand-50  transition-all bg-white dark:bg-transparent"
                >
                    <Plus size={24} />
                    <span className="text-[10px] mt-1 font-medium">Add Photo</span>
                </button>
                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleAddImage} />
            </div>
        </div>
      </div>

      {/* RIGHT COLUMN: Form */}
      <div className="lg:w-1/2 h-full flex flex-col lg:border-l border-gray-200 bg-white  relative transition-colors duration-300">
        
        {/* Tabs Navigation */}
        <div className="flex border-b border-gray-200  px-4 lg:px-8 pt-2 shrink-0 bg-white  transition-colors duration-300">
            {['details', 'specs', 'logistics', 'variants'].map((tab) => (
                <button
                    key={tab}
                    onClick={() => setActiveTab(tab as any)}
                    className={`flex-1 py-4 text-xs lg:text-sm font-medium uppercase tracking-wider transition-colors relative ${activeTab === tab ? 'text-brand-600 ' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                >
                    {tab}
{activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-500 rounded-t-full" />}
                </button>
            ))}
        </div>

        {/* Scrollable Form Content */}
<div className="flex-1 overflow-y-auto p-6 lg:p-8 pb-32 custom-scrollbar">
    
    {/* DETAILS TAB */}
    {activeTab === 'details' && (
        <div className="space-y-6 animate-in slide-in-from-bottom-2 duration-500">
            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Product Title</label>
                <input 
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    className="w-full bg-transparent border-b border-gray-200 py-2 text-xl font-medium text-gray-900  focus:border-brand-500 focus:outline-none transition-colors placeholder:text-gray-400"
                    placeholder="Enter product name..."
                />
            </div>

            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Description</label>
                <textarea 
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={4}
                    className="w-full bg-white /50 rounded-xl border border-gray-200 p-4 text-sm text-gray-700 dark:text-gray-300 focus:border-brand-500 dark:focus:border-brand-500 focus:outline-none transition-colors placeholder:text-gray-400 resize-none leading-relaxed focus:bg-white dark:focus:bg-gray-800"
                    placeholder="Describe your product..."
                />
            </div>

            <div className="grid grid-cols-2 gap-6">
                 <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</label>
                    <input 
                        value={formData.brand}
                        onChange={(e) => handleChange('brand', e.target.value)}
                        className="w-full bg-white /50 rounded-xl border border-gray-200 p-3 text-sm text-gray-900  focus:border-brand-500 focus:outline-none focus:bg-white dark:focus:bg-gray-800 transition-colors"
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Condition</label>
                    <select 
                        value={formData.condition}
                        onChange={(e) => handleChange('condition', e.target.value)}
                        className="w-full bg-white /50 rounded-xl border border-gray-200 p-3 text-sm text-gray-900  focus:border-brand-500 focus:outline-none appearance-none focus:bg-white dark:focus:bg-gray-800 transition-colors"
                    >
                        <option value="New">New</option>
                        <option value="Used">Used</option>
                        <option value="Refurbished">Refurbished</option>
                    </select>
                </div>
            </div>
            
            <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-white /30 border border-gray-200 dark:border-gray-700/50">
                     <input 
                        type="checkbox" 
                        id="isDigital"
                        checked={formData.isDigital}
                        onChange={(e) => handleChange('isDigital', e.target.checked)}
                        className="w-5 h-5 rounded bg-white dark:bg-gray-700 border-gray-300 text-brand-600 focus:ring-brand-500"
                     />
                     <label htmlFor="isDigital" className="text-sm text-gray-700 dark:text-gray-300 font-medium cursor-pointer">This is a Digital Product / Service</label>
                </div>

                {formData.isDigital && (
                    <div className="p-4 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 space-y-4 animate-in slide-in-from-top-2">
                        {/* File Upload Option */}
                        <div>
                            <label className="block text-sm text-purple-700 mb-2 font-medium">Upload Digital File</label>
                            <div className={`relative border-2 border-dashed rounded-lg p-4 transition-all duration-200 ${
                                uploadError 
                                ? 'border-red-300 bg-red-50' 
                                : uploadProgress > 0 && uploadProgress < 100
                                    ? 'border-purple-300 bg-purple-50'
                                    : 'border-gray-300 bg-white hover:border-purple-400 hover:bg-purple-50'
                            }`}>
                                <input
                                    ref={digitalFileInputRef}
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.zip"
                                    onChange={handleDigitalFileChange}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    disabled={uploadProgress > 0 && uploadProgress < 100}
                                />
                                <div className="text-center">
                                    <Upload className="mx-auto h-8 w-8 text-gray-400" />
                                    <div className="mt-2">
                                        <p className="text-sm font-medium text-gray-900">
                                            {uploadProgress > 0 && uploadProgress < 100 ? 'Uploading...' : 'Click to upload'}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            PDF, images (JPEG, PNG, GIF, WebP), or ZIP files. Max 100MB.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Upload Progress */}
                            {uploadProgress > 0 && uploadProgress < 100 && (
                                <div className="space-y-2 mt-3">
                                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                                        <div 
                                            className="bg-purple-600 h-2.5 rounded-full transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                    </div>
                                    <p className="text-xs text-gray-600 text-center">
                                        Uploading... {uploadProgress}%
                                    </p>
                                </div>
                            )}

                            {/* Upload Error Message */}
                            {uploadError && (
                                <div className="flex items-center bg-red-50 border border-red-200 rounded-lg px-4 py-3 mt-3">
                                    <X className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                                    <span className="text-red-700 text-sm font-medium">{uploadError}</span>
                                </div>
                            )}

                            {/* Upload Success */}
                            {uploadProgress === 100 && (
                                <div className="flex items-center bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mt-3">
                                    <Check className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                                    <span className="text-blue-700 text-sm font-medium">File uploaded successfully!</span>
                                </div>
                            )}
                        </div>

                        {/* OR Separator */}
                        <div className="flex items-center">
                            <div className="flex-1 border-t border-gray-300"></div>
                            <span className="px-4 text-sm text-gray-500">OR</span>
                            <div className="flex-1 border-t border-gray-300"></div>
                        </div>

                        {/* External URL Option */}
                        <div>
                            <label className="block text-sm text-purple-700 mb-2 font-medium">External Download URL</label>
                            <input
                                type="url"
                                className={`w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-700 focus:border-purple-700 transition-all ${hasUploadedFile ? "bg-purple-100 text-purple-700" : ""}`}
                                value={formData.digitalProductLink || ''}
                                onChange={(e) => handleChange('digitalProductLink', e.target.value)}
                                placeholder="https://example.com/download/file.pdf"
                                disabled={isUploading || hasUploadedFile}
                            />
                            {!hasUploadedFile && (
                                <p className="text-gray-500 mt-2 text-sm">
                                    Provide a direct download link to your file
                                </p>
                            )}
                        </div>

                        {/* File Preview */}
                        {(formData.digitalProductLink || hasUploadedFile) && (
                            <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        {digitalFilePreview ? (
                                            <img
                                                src={digitalFilePreview}
                                                alt="File preview"
                                                className="w-12 h-12 object-cover rounded-lg mr-3"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                                                <FileText className="w-6 h-6 text-purple-600" />
                                            </div>
                                        )}
                                        <div>
                                            <p className="text-sm font-medium text-purple-900">
                                                {formData.digitalFileName || 'External File'}
                                            </p>
                                            <p className="text-xs text-purple-700">
                                                {formData.digitalFileSize ? `${(formData.digitalFileSize / (1024 * 1024)).toFixed(2)} MB` : 'External download'}
                                            </p>
                                            {formData.digitalProductLink && (
                                                <p className="text-xs text-purple-600 truncate max-w-xs">
                                                    {formData.digitalProductLink}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={removeDigitalFile}
                                        className="text-red-600 hover:text-red-800 transition-colors"
                                        disabled={isUploading}
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

             <div className="space-y-3">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Tags</label>
                <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, i) => (
                        <span key={i} className="inline-flex items-center px-3 py-1 rounded-lg bg-gray-100  text-gray-700 dark:text-gray-300 text-sm border border-gray-200 dark:border-gray-700">
                            {tag}
                            <button onClick={() => handleChange('tags', formData.tags.filter((_, idx) => idx !== i))} className="ml-2 hover:text-red-500"><X size={12} /></button>
                        </span>
                    ))}
                     <input 
                        placeholder="+ Add tag"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                const val = e.currentTarget.value.trim();
                                if(val && !formData.tags.includes(val)) handleChange('tags', [...formData.tags, val]);
                                e.currentTarget.value = '';
                            }
                        }}
                        className="bg-transparent border-b border-dashed border-gray-300 py-1 px-2 text-sm focus:outline-none focus:border-brand-500 w-24 text-gray-700 dark:text-gray-300 placeholder:text-gray-400"
                    />
                </div>
            </div>
        </div>
    )}

    {/* ATTRIBUTES TAB */}
    {activeTab === 'specs' && (
         <div className="space-y-6 animate-in slide-in-from-bottom-2 duration-500">
            <AttributeEditor label="Colors" icon={<Palette size={14}/>} items={formData.colors} onAdd={v => handleArrayAdd('colors', v)} onRemove={i => handleArrayRemove('colors', i)} />
            <AttributeEditor label="Sizes" icon={<Ruler size={14}/>} items={formData.sizes} onAdd={v => handleArrayAdd('sizes', v)} onRemove={i => handleArrayRemove('sizes', i)} />
            <AttributeEditor label="Materials" icon={<Layers size={14}/>} items={formData.materials} onAdd={v => handleArrayAdd('materials', v)} onRemove={i => handleArrayRemove('materials', i)} />
            <AttributeEditor label="Styles" icon={<Scissors size={14}/>} items={formData.styles} onAdd={v => handleArrayAdd('styles', v)} onRemove={i => handleArrayRemove('styles', i)} />
            <AttributeEditor label="Patterns" icon={<Grid size={14}/>} items={formData.patterns} onAdd={v => handleArrayAdd('patterns', v)} onRemove={i => handleArrayRemove('patterns', i)} />
         </div>
    )}

    {/* LOGISTICS TAB */}
    {activeTab === 'logistics' && (
         <div className="space-y-6 animate-in slide-in-from-bottom-2 duration-500">
             <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1"><Box size={12}/> Dimensions</label>
                    <input value={formData.dimensions} onChange={e => handleChange('dimensions', e.target.value)} placeholder="10x10x10 cm" className="w-full bg-white /50 rounded-xl border border-gray-200 p-3 text-sm text-gray-900  focus:border-brand-500 focus:outline-none" />
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1"><Box size={12}/> Weight</label>
                    <input value={formData.weight} onChange={e => handleChange('weight', e.target.value)} placeholder="0.5 kg" className="w-full bg-white /50 rounded-xl border border-gray-200 p-3 text-sm text-gray-900  focus:border-brand-500 focus:outline-none" />
                </div>
             </div>

              {!formData.isDigital && (
                <>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-200">
                    <div>
                      <p className="text-xs font-medium text-gray-700">Make negotiable</p>
                      <p className="text-[10px] text-gray-500">Let buyers propose prices</p>
                    </div>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.acceptNegotiations || false}
                        onChange={(e) => handleChange('acceptNegotiations', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="relative w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-black"></div>
                    </label>
                  </div>

                  {formData.acceptNegotiations && (
                    <div className="grid grid-cols-2 gap-3 pl-3 animate-in slide-in-from-top-2">
                      <div className="space-y-1">
                        <label className="text-[10px] font-medium text-gray-500 uppercase">Min Price (₦)</label>
                        <input
                          type="number"
                          value={formData.minPrice || ''}
                          onChange={e => handleChange('minPrice', parseFloat(e.target.value) || 0)}
                          className="w-full bg-white rounded-lg border border-gray-200 p-2.5 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="Lowest"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-medium text-gray-500 uppercase">Max Price (₦)</label>
                        <input
                          type="number"
                          value={formData.maxPrice || ''}
                          onChange={e => handleChange('maxPrice', parseFloat(e.target.value) || 0)}
                          className="w-full bg-white rounded-lg border border-gray-200 p-2.5 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="Highest"
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-3 p-3 rounded-xl bg-gray-50 border border-gray-200">
                    <p className="text-xs font-medium text-gray-700">Delivery Options</p>
                    <div className="flex gap-3">
                      <label className="flex-1 flex items-center gap-2 p-2.5 rounded-lg bg-white border border-gray-200 cursor-pointer has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50">
                        <input
                          type="checkbox"
                          checked={formData.availableForPickup !== false}
                          onChange={(e) => handleChange('availableForPickup', e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-xs font-medium text-gray-700">Pickup</span>
                      </label>
                      <label className="flex-1 flex items-center gap-2 p-2.5 rounded-lg bg-white border border-gray-200 cursor-pointer has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50">
                        <input
                          type="checkbox"
                          checked={formData.availableForDelivery !== false}
                          onChange={(e) => handleChange('availableForDelivery', e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-xs font-medium text-gray-700">Delivery</span>
                      </label>
                    </div>
                    {formData.availableForDelivery && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-[10px] font-medium text-gray-500">Lagos Fee (₦)</label>
                          <input
                            type="number"
                            value={formData.lagosDeliveryFee || ''}
                            onChange={e => handleChange('lagosDeliveryFee', parseFloat(e.target.value) || 0)}
                            className="w-full bg-white rounded-lg border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] font-medium text-gray-500">Outside Lagos (₦)</label>
                          <input
                            type="number"
                            value={formData.outsideLagosDeliveryFee || ''}
                            onChange={e => handleChange('outsideLagosDeliveryFee', parseFloat(e.target.value) || 0)}
                            className="w-full bg-white rounded-lg border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="space-y-1">
                 <label className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1"><Truck size={12}/> Delivery Option</label>
                 <input value={formData.deliveryOption} onChange={e => handleChange('deliveryOption', e.target.value)} className="w-full bg-white /50 rounded-xl border border-gray-200 p-3 text-sm text-gray-900  focus:border-brand-500 focus:outline-none" />
              </div>
              
              <div className="space-y-1">
                 <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Timeline</label>
                 <select
                   value={formData.deliveryTimeline}
                   onChange={e => handleTimelineSelect(e.target.value)}
                   className="w-full bg-white /50 rounded-xl border border-gray-200 p-3 text-sm text-gray-900  focus:border-brand-500 focus:outline-none"
                 >
                     {DELIVERY_TIMELINES.map((t) => (
                         <option key={t.value} value={t.value}>{t.label}</option>
                     ))}
                 </select>
              </div>

              <div className="space-y-1">
                 <label className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1"><ShieldCheck size={12}/> Return Policy</label>
                   <select
                       id="returnPolicy"
                       className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                       value={formData.returnPolicy}
                       onChange={handleReturnPolicyChange}
                     >
                       {RETURN_POLICIES.map(r => (
                         <option key={r.value} value={r.value}>{r.label}</option>
                       ))}
                     </select>
              </div>
         </div>
    )}

    {/* VARIANTS TAB */}
    {activeTab === 'variants' && (
         <div className="space-y-6 animate-in slide-in-from-bottom-2 duration-500">
             
            <div className="p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 flex flex-col items-center text-center space-y-3">
                <p className="text-sm text-blue-700 dark:text-blue-200">
                    Automatically generate options based on attributes (Color, Size, Material, Style, etc).
                </p>
                <Button onClick={generateVariants} variant="outline" size="sm" icon={<RefreshCcw size={14}/>}>
                    Generate Variants
                </Button>
            </div>

             <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Global Stock</label>
                <input type="number" value={formData.stock} onChange={e => handleChange('stock', parseInt(e.target.value))} className="w-full bg-white /50 rounded-xl border border-gray-200 p-3 text-sm text-gray-900  focus:border-brand-500 focus:outline-none" />
             </div>

             {formData.variants.length > 0 && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">All Variants ({formData.variants.length})</label>
                        <button onClick={() => handleChange('variants', [])} className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1"><Trash2 size={12}/> Clear</button>
                    </div>
                    <div className="space-y-2">
                        {formData.variants.map((variant) => (
                            <div key={variant.id} className="flex items-center gap-3 bg-white /30 border border-gray-200 p-3 rounded-xl">
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900  truncate">{variant.name}</p>
                                    <p className="text-[10px] text-gray-500 font-mono truncate">{Object.keys(variant.attributes).join(', ')}</p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <div className="flex flex-col items-end">
                                        <label className="text-[9px] text-gray-500 uppercase">Price</label>
                                        <input 
                                            type="number" 
                                            value={variant.price} 
                                            onChange={(e) => updateVariant(variant.id, 'price', parseFloat(e.target.value))}
                                            className="w-20 bg-gray-100 dark:bg-black/20 border border-gray-300 rounded px-1 py-0.5 text-xs text-right text-gray-900  focus:border-brand-500 focus:outline-none" 
                                        />
                                    </div>
                                    <div className="flex flex-col items-end">
                                        <label className="text-[9px] text-gray-500 uppercase">Stock</label>
                                        <input 
                                            type="number" 
                                            value={variant.stock}
                                            onChange={(e) => updateVariant(variant.id, 'stock', parseInt(e.target.value))}
                                            className="w-14 bg-gray-100 dark:bg-black/20 border border-gray-300 rounded px-1 py-0.5 text-xs text-right text-gray-900  focus:border-brand-500 focus:outline-none"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
             )}
         </div>
    )}
</div>

        {/* Sticky Footer */}
        <div className=" bottom-0 left-0 right-0 p-6 lg:p-8 border-t border-gray-200 backdrop-blur-md z-20 transition-colors duration-300">
            <Button 
                fullWidth 
                onClick={() => onConfirm(formData)}
                className="h-14 text-lg shadow-xl shadow-brand-500/20"
                icon={<Check size={20}/>}
                disabled={isUploading || (formData.isDigital && !formData.digitalProductLink && !hasUploadedFile)}
            >
                {isUploading ? 'Uploading File...' : formData.isDigital && !formData.digitalProductLink && !hasUploadedFile ? 'Upload a file or provide a URL' : 'Launch Storefront'}
            </Button>
        </div>

      </div>
    </div>
  );
};

const AttributeEditor: React.FC<{
    label: string; 
    icon: React.ReactNode; 
    items: string[]; 
    onAdd: (v: string) => void; 
    onRemove: (i: number) => void;
}> = ({ label, icon, items, onAdd, onRemove }) => {
    return (
        <div className="space-y-2">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
                {icon} {label}
            </label>
            <div className="flex flex-wrap gap-2 p-3 rounded-xl bg-white /30 border border-gray-200 dark:border-gray-700/50 min-h-[3rem]">
                {items.map((item, i) => (
                    <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs border border-gray-300 shadow-sm">
                        {item}
                        <button onClick={() => onRemove(i)} className="ml-1.5 text-gray-400 hover:text-red-500"><X size={10} /></button>
                    </span>
                ))}
                <input 
                    placeholder="Add..."
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            onAdd(e.currentTarget.value.trim());
                            e.currentTarget.value = '';
                        }
                    }}
                    className="bg-transparent border-none py-1 px-1 text-sm focus:outline-none focus:ring-0 w-20 placeholder:text-gray-400 text-gray-900 "
                />
            </div>
        </div>
    );
}